/*
 * Customer reviews rail.
 *
 * The sliding itself is the browser's: the track is an overflow-x scroll-snap
 * container, so swipe, momentum, trackpad and the arrow keys work before this
 * file loads and keep working if it fails. What is added here is the part CSS
 * cannot do - how many cards make a page at the current breakpoint, the dots
 * that follow it, the five-second advance, and the fade that sets the current
 * page apart from the ones either side of it.
 *
 * Autoplay is a courtesy, not a demand: it stops for a pointer, for keyboard
 * focus, while a finger is on the track, when the section scrolls out of view,
 * and entirely for anyone who has asked for reduced motion.
 */
(function () {
  'use strict';

  if (window.__lts2ReviewsReady) return;
  window.__lts2ReviewsReady = true;

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(root) {
    if (root.dataset.ltsReady) return;
    root.dataset.ltsReady = '1';

    var track = root.querySelector('[data-lts2-rev-track]');
    var dotsBox = root.querySelector('[data-lts2-rev-dots]');
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-lts2-rev-card]'));
    if (!track || !cards.length) return;

    var delay = parseInt(root.getAttribute('data-autoplay'), 10) || 0;
    var perView = 1;
    var pages = 1;
    var page = 0;
    var timer = null;
    var held = false;          // pointer down, hover, or focus inside
    var onScreen = true;
    var raf = 0;

    /* The breakpoint owns the column count, so read it back from the custom
       property rather than duplicating the media queries here. */
    function readPerView() {
      var declared = parseInt(getComputedStyle(root.closest('.lts2-rev') || root)
        .getPropertyValue('--rev-per-view'), 10);
      if (declared > 0) return declared;
      var step = cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : cards[0].offsetWidth;
      return Math.max(1, Math.round(track.clientWidth / step));
    }

    function maxScroll() {
      return Math.max(0, track.scrollWidth - track.clientWidth);
    }

    /* Where a page actually comes to rest.

       Ten cards three at a time is three and a third pages, so the last page's
       card sits further right than the track can scroll. The browser clamps the
       scroll silently; if this returned the unclamped offset, the last dot could
       never match where the track stopped. Clamping here keeps the two in step. */
    function pageStart(p) {
      var i = Math.min(p * perView, cards.length - 1);
      return Math.min(cards[i].offsetLeft - cards[0].offsetLeft, maxScroll());
    }

    function buildDots() {
      if (!dotsBox) return;
      dotsBox.innerHTML = '';
      if (pages < 2) return;
      var label = dotsBox.getAttribute('data-label-single') || 'Go to slide';
      for (var i = 0; i < pages; i++) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'lts2-rev__dot';
        b.setAttribute('role', 'tab');
        b.setAttribute('aria-label', label + ' ' + (i + 1));
        b.setAttribute('aria-selected', i === page ? 'true' : 'false');
        (function (n) {
          b.addEventListener('click', function () { go(n); pauseFor(delay); });
        })(i);
        dotsBox.appendChild(b);
      }
    }

    function syncDots() {
      if (!dotsBox) return;
      var all = dotsBox.children;
      for (var i = 0; i < all.length; i++) {
        all[i].setAttribute('aria-selected', i === page ? 'true' : 'false');
      }
    }

    /* The fade. Cards on screen stand at full strength and the rest sit back,
       which reads as a cross-fade as the track slides.

       Driven by what is actually inside the track's viewport, not by
       page * perView. The two disagree whenever the browser clamps the scroll -
       on the last page of an uneven set it shows the final three cards while
       the arithmetic still points at the previous three, which left a card
       visible and dimmed. Measuring cannot drift from what the eye sees, and it
       is also correct mid-swipe, between snap points. */
    function syncFade() {
      var left = track.scrollLeft;
      var right = left + track.clientWidth;
      var origin = cards[0].offsetLeft;
      var anyHidden = track.scrollWidth > track.clientWidth + 2;
      for (var i = 0; i < cards.length; i++) {
        var cl = cards[i].offsetLeft - origin;
        var cr = cl + cards[i].offsetWidth;
        /* A 2px tolerance so a rounding sliver at the edge does not count as
           on screen - and nothing is dimmed when the whole rail fits. */
        var onScreenNow = cr > left + 2 && cl < right - 2;
        cards[i].classList.toggle('is-dim', anyHidden && !onScreenNow);
      }
    }

    /* The fade is deliberately not set here. The scroll is asynchronous, so
       reading it now would describe where the track still is rather than where
       it is going; the scroll handler paints it as the movement happens. */
    function go(p) {
      page = Math.max(0, Math.min(p, pages - 1));
      track.scrollTo({ left: pageStart(page), behavior: REDUCED ? 'auto' : 'smooth' });
      syncDots();
    }

    function measure() {
      var next = readPerView();
      var nextPages = Math.max(1, Math.ceil(cards.length / next));
      if (next === perView && nextPages === pages) return;
      perView = next;
      pages = nextPages;
      page = Math.min(page, pages - 1);
      buildDots();
      syncFade();
    }

    /* Which page the track has actually come to rest on - the source of truth
       after a swipe, which never went through go().

       Nearest resting place rather than scrollLeft / pageWidth: the last page's
       start is clamped to the end of the track, so a division can never round
       up to it and the final dot would never light. */
    function readPage() {
      raf = 0;
      var best = 0;
      var bestDist = Infinity;
      for (var p = 0; p < pages; p++) {
        var d = Math.abs(track.scrollLeft - pageStart(p));
        if (d < bestDist) { bestDist = d; best = p; }
      }
      if (best !== page) { page = best; syncDots(); }
      syncFade();
    }

    function tick() {
      if (held || !onScreen || document.hidden) return;
      go(page + 1 >= pages ? 0 : page + 1);
    }

    function start() {
      if (!delay || REDUCED || pages < 2) return;
      stop();
      timer = setInterval(tick, delay);
    }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    function pauseFor(ms) { stop(); if (ms) setTimeout(start, ms); }

    /* ---- wiring ---- */

    track.addEventListener('scroll', function () {
      if (!raf) raf = requestAnimationFrame(readPage);
    }, { passive: true });

    ['pointerenter', 'focusin'].forEach(function (e) {
      root.addEventListener(e, function () { held = true; stop(); });
    });
    ['pointerleave', 'focusout'].forEach(function (e) {
      root.addEventListener(e, function () {
        if (root.contains(document.activeElement) || root.matches(':hover')) return;
        held = false; start();
      });
    });
    track.addEventListener('touchstart', function () { held = true; stop(); }, { passive: true });
    track.addEventListener('touchend', function () {
      held = false;
      /* Let the swipe settle before the timer can move it again. */
      pauseFor(delay);
    }, { passive: true });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop(); else start();
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        onScreen = entries[0].isIntersecting;
        if (onScreen) start(); else stop();
      }, { threshold: 0.15 }).observe(root);
    }

    var resizeRaf = 0;
    window.addEventListener('resize', function () {
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(function () { measure(); go(page); });
    }, { passive: true });

    measure();
    syncDots();
    syncFade();
    start();
  }

  function boot() {
    var all = document.querySelectorAll('[data-lts2-reviews]');
    for (var i = 0; i < all.length; i++) init(all[i]);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* The theme editor re-renders a section in place; re-init the new copy. */
  document.addEventListener('shopify:section:load', boot);
})();
